import { useEffect, useMemo, useState } from 'react'

import { cn } from '@/libs/tw'
import { useCartStore } from '@/stores/cart-store/cartStore'
import { CartService } from '@/stores/cart-store/slices/types'

import { useRoomContext } from '../../hooks/useRoomContext'
import { useOffers } from '../../hooks/useRoomOffers'
import { Rate, RoomOffer } from '../../types'
import { Room } from './room'

const sortRoomOffers = (offers?: RoomOffer[], suggestedRoomCode?: string) => {
	if (!offers || !suggestedRoomCode) return offers ?? []

	const suggestedRoom = offers.find((room) => room.roomTypeCode === suggestedRoomCode)
	if (!suggestedRoom) return offers

	return [suggestedRoom, ...offers.filter((room) => room.roomTypeCode !== suggestedRoomCode)]
}

export const RoomPicker = () => {
	const { roomConfig, selectedRoom } = useRoomContext()
	const setRoom = useCartStore.use.setRoom()
	const { selectedRoomCodes } = useCartStore.use.roomSelection()

	const [selectedRate, setSelectedRate] = useState<string>()
	const [selectedServices, setSelectedServices] = useState<{ id: string; quantity: number }[]>([])

	const otherSelectedRoomCodes = selectedRoomCodes
		.filter((code) => code.roomConfigId !== roomConfig.id)
		.map((code) => code.roomCode)

	const { data: offers, isSuccess } = useOffers({
		adults: roomConfig.adults,
		childrenAges: roomConfig.children,
		selectedRoomCodes: otherSelectedRoomCodes,
	})

	const rooms = useMemo(
		() => sortRoomOffers(offers?.roomTypeOffers, roomConfig.roomSuggestion?.roomTypeCode),
		[offers, roomConfig]
	)

	const handleRoomSelection = (roomOffer: RoomOffer, rate: Rate, services?: CartService[]) => {
		setRoom({
			roomConfigId: roomConfig.id,
			room: {
				title: roomOffer.title,
				roomTypeCode: roomOffer.roomTypeCode,
				image: {
					src: roomOffer.images[0].largeUrl,
					alt: roomOffer.images[0].alt,
					title: roomOffer.images[0].title,
				},
				rate: {
					title: rate.title,
					ratePlanCode: rate.ratePlanCode,
					cancellationDeadline: rate.cancellationDeadline,
					price: rate.price,
				},
				services:
					services?.map((s) => ({
						title: s.title,
						serviceCode: s.serviceCode,
						price: s.price,
						servicePricing: s.servicePricing,
						quantity: s.quantity,
						maxQuantity: s.maxQuantity,
						flexibleQuantity: s.flexibleQuantity,
					})) ?? [],
			},
		})
	}

	const handleServiceSelection = (serviceCode: string, quantity: number) => {
		const isSelected = selectedServices.find((s) => s.id === serviceCode)

		if (isSelected) {
			if (quantity === 0) {
				return setSelectedServices(selectedServices.filter((s) => s.id !== serviceCode))
			}
			if (isSelected.quantity !== quantity) {
				return setSelectedServices(
					selectedServices.map((s) => (s.id === serviceCode ? { ...s, quantity } : s))
				)
			}
			return setSelectedServices(selectedServices.filter((s) => s.id !== serviceCode))
		}

		setSelectedServices([...selectedServices, { id: serviceCode, quantity }])
	}

	useEffect(() => {
		if (selectedRoom) {
			setSelectedRate(selectedRoom.roomTypeCode + '-' + selectedRoom.rate.ratePlanCode)
			setSelectedServices(
				selectedRoom.services.map((service) => ({
					id: service.serviceCode,
					quantity: service.quantity ?? 0,
				}))
			)
		}
	}, [selectedRoom])

	return (
		<div
			className={cn(
				'grid grid-cols-1 items-start gap-24 text-roomSelection-textColor lg:grid-cols-2'
			)}
		>
			{!rooms.length && !isSuccess && <div className="h-[1000px]" />}
			{rooms.map((room) => (
				<Room
					key={room.roomTypeCode}
					room={room}
					selectedRate={selectedRate}
					selectedServices={selectedServices}
					onSelect={handleRoomSelection}
					onSelectRate={setSelectedRate}
					onSelectService={handleServiceSelection}
				/>
			))}
		</div>
	)
}
